import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Row,
  Col,
  Statistic,
  Typography,
  Flex,
  Tooltip,
  Empty,
  Spin,
} from 'antd';
import { FireOutlined, TrophyOutlined, CheckCircleOutlined, BookOutlined } from '@ant-design/icons';
import {
  computeDashboardStats,
  DashboardStats,
  FSRSState,
  TranslationLanguage,
} from '@lingoping/core';
import { StorageManager } from '../storage/index.js';
import { cardStageLabel, formatStatDay, plural } from '../options/format.js';
import { Echo } from './Echo.js';
import { UpNextCard } from './UpNextCard.js';
import { fillPendingCards } from '../services/pendingCards.js';
import { CouldntAdd, takeCouldntAdd } from '../services/pendingCardStore.js';
import { getBaseTranslationLanguage } from '../services/translationSettings.js';

const { Text } = Typography;

const STATE_COLORS: Record<FSRSState, string> = {
  New: '#06b6d4',
  Learning: '#eab308',
  Review: '#16a34a',
  Relearning: '#dc2626',
};

const AXIS_LABEL_COLOR = '#64748b';

/** Minimal inline bar chart — no charting dependency needed for 7-14 data points. */
const MiniBarChart: React.FC<{
  data: { date: string; count: number }[];
  color: string;
  emptyLabel: string;
  /** Singular noun for the tooltip and screen readers, e.g. "review". */
  unit: string;
  /** Label the first bar "Today" (for forecasts that start today). */
  firstIsToday?: boolean;
  ariaLabel: string;
}> = ({ data, color, emptyLabel, unit, firstIsToday, ariaLabel }) => {
  const max = Math.max(1, ...data.map((d) => d.count));
  const allZero = data.every((d) => d.count === 0);

  if (allZero) {
    return (
      <Flex justify="center" align="center" style={{ height: 90 }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          {emptyLabel}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex
      align="flex-end"
      gap={6}
      style={{ height: 90, padding: '4px 0' }}
      role="group"
      aria-label={ariaLabel}
    >
      {data.map((d, i) => {
        const summary = `${formatStatDay(d.date, 'long')}: ${plural(d.count, unit)}`;
        return (
          <Tooltip key={d.date} title={summary}>
            <Flex
              vertical
              align="center"
              style={{ flex: 1 }}
              gap={4}
              aria-label={summary}
              role="img"
            >
              <div
                style={{
                  width: '100%',
                  height: Math.max(3, Math.round((d.count / max) * 64)),
                  background: d.count > 0 ? color : '#e2e8f0',
                  borderRadius: 3,
                  transition: 'height 0.2s ease',
                }}
              />
              <Text style={{ fontSize: 11, color: AXIS_LABEL_COLOR }}>
                {firstIsToday && i === 0 ? 'Today' : formatStatDay(d.date)}
              </Text>
            </Flex>
          </Tooltip>
        );
      })}
    </Flex>
  );
};

const StageBreakdownBar: React.FC<{ breakdown: Record<FSRSState, number>; total: number }> = ({
  breakdown,
  total,
}) => {
  const states: FSRSState[] = ['New', 'Learning', 'Review', 'Relearning'];

  return (
    <Flex vertical gap={8}>
      <Flex style={{ width: '100%', height: 14, borderRadius: 7, overflow: 'hidden' }}>
        {states.map((s) =>
          breakdown[s] > 0 ? (
            <Tooltip key={s} title={`${cardStageLabel(s)}: ${plural(breakdown[s], 'card')}`}>
              <div
                style={{
                  width: `${(breakdown[s] / total) * 100}%`,
                  background: STATE_COLORS[s],
                  height: '100%',
                }}
              />
            </Tooltip>
          ) : null
        )}
      </Flex>
      <Flex wrap="wrap" gap={16}>
        {states.map((s) => (
          <Flex key={s} align="center" gap={6}>
            <div
              aria-hidden="true"
              style={{ width: 10, height: 10, borderRadius: 2, background: STATE_COLORS[s] }}
            />
            <Text style={{ fontSize: 13, color: '#475569' }}>
              {cardStageLabel(s)} ({breakdown[s]})
            </Text>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};

const StatCard: React.FC<{
  title: string;
  value: number | string;
  suffix?: string;
  color: string;
  icon: React.ReactNode;
  hint: string;
}> = ({ title, value, suffix, color, icon, hint }) => (
  <Card style={{ height: '100%' }}>
    <Statistic
      title={title}
      value={value}
      suffix={suffix}
      styles={{ content: { color } }}
      prefix={icon}
    />
    <Text type="secondary" style={{ fontSize: 12 }}>
      {hint}
    </Text>
  </Card>
);

interface Props {
  /** Bump to reload the numbers (e.g. after the options page refreshes its data). */
  refreshKey?: number;
  /** Shown on the empty state so a new user has an obvious next step. */
  onGoToDecks?: () => void;
}

export const DashboardTab: React.FC<Props> = ({ refreshKey = 0, onGoToDecks }) => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  /** Cards the learner has, including chosen words still being prepared (not retired ones). */
  const [hasCards, setHasCards] = useState(false);
  const [error, setError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [language, setLanguage] = useState<TranslationLanguage>('en');
  /** Bumped when a card changed here (a pending card prepared, a word removed from Up next). */
  const [localChange, setLocalChange] = useState(0);
  /** Chosen words that turned out not to be German, shown once. */
  const [couldntAdd, setCouldntAdd] = useState<CouldntAdd[]>([]);

  // Chosen words whose card text is still being prepared (offline earlier, the daily limit) are
  // filled in whenever the dashboard opens; words that are not German are reported once.
  useEffect(() => {
    let isMounted = true;
    void fillPendingCards()
      .then(async (result) => {
        const dropped = await takeCouldntAdd();
        if (!isMounted) return;
        if (dropped.length > 0) setCouldntAdd((prev) => [...prev, ...dropped]);
        if (result.filled > 0 || result.removed > 0) setLocalChange((n) => n + 1);
      })
      .catch(() => undefined);
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function load() {
      try {
        const [cards, logs, shownLanguage] = await Promise.all([
          StorageManager.getCards(),
          StorageManager.getReviewLogs(),
          getBaseTranslationLanguage(),
        ]);
        if (isMounted) {
          setStats(computeDashboardStats(cards, logs));
          setHasCards(cards.some((c) => !c.retired_at));
          setLanguage(shownLanguage);
          setError(false);
        }
      } catch (err) {
        console.error('Failed to load dashboard stats:', err);
        if (isMounted) setError(true);
      }
    }
    load();
    return () => {
      isMounted = false;
    };
  }, [refreshKey, attempt, localChange]);

  if (error && !stats) {
    return (
      <Alert
        type="error"
        showIcon
        title="We couldn't load your progress."
        action={
          <Button size="small" onClick={() => setAttempt((a) => a + 1)}>
            Try again
          </Button>
        }
      />
    );
  }

  if (!stats) {
    return (
      <Flex justify="center" align="center" style={{ minHeight: 300 }}>
        <Spin aria-label="Loading your progress" />
      </Flex>
    );
  }

  if (!hasCards && stats.totalReviews === 0) {
    return (
      <Card>
        <Empty
          image={<Echo mood="hello" size={120} style={{ margin: '0 auto' }} />}
          styles={{ image: { height: 'auto' } }}
          description={
            <Flex vertical gap={4}>
              <Text strong>Your progress will show up here</Text>
              <Text type="secondary">Follow a deck or add a word to get your first cards.</Text>
            </Flex>
          }
        >
          {onGoToDecks && (
            <Button type="primary" onClick={onGoToDecks}>
              Go to Decks
            </Button>
          )}
        </Empty>
      </Card>
    );
  }

  return (
    <Flex vertical gap={16}>
      {couldntAdd.length > 0 && (
        <Alert
          type="warning"
          showIcon
          closable={{ onClose: () => setCouldntAdd([]) }}
          title={
            couldntAdd.length === 1
              ? `“${couldntAdd[0].term}” couldn't be added`
              : `${couldntAdd.length} words couldn't be added`
          }
          description={
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {couldntAdd.map((c) => (
                <li key={`${c.term}-${c.at}`}>{c.reason}</li>
              ))}
            </ul>
          }
        />
      )}
      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <StatCard
            title="Current streak"
            value={stats.currentStreakDays}
            suffix={stats.currentStreakDays === 1 ? 'day' : 'days'}
            color="#c2410c"
            icon={<FireOutlined />}
            hint="Days in a row with a review"
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            title="Longest streak"
            value={stats.longestStreakDays}
            suffix={stats.longestStreakDays === 1 ? 'day' : 'days'}
            color="#6d28d9"
            icon={<TrophyOutlined />}
            hint="Your best run so far"
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            title="Remembered"
            value={stats.totalReviews === 0 ? '–' : `${Math.round(stats.retentionRate * 100)}%`}
            color="#15803d"
            icon={<CheckCircleOutlined />}
            hint={
              stats.totalReviews === 0
                ? 'Appears after your first review'
                : 'Reviews you did not answer “Again”'
            }
          />
        </Col>
        <Col xs={12} md={6}>
          <StatCard
            title="Well learned"
            value={stats.matureCards}
            suffix={`/ ${stats.totalCards}`}
            color="#1d4ed8"
            icon={<BookOutlined />}
            hint="Cards you remember for 3+ weeks"
          />
        </Col>
      </Row>

      <UpNextCard
        refreshKey={refreshKey + localChange}
        language={language}
        onChanged={() => setLocalChange((n) => n + 1)}
      />

      <Row gutter={[16, 16]}>
        <Col xs={24} md={12}>
          <Card title="Reviews in the last 14 days" size="small">
            <MiniBarChart
              data={stats.reviewsLast14Days}
              color="#1677ff"
              unit="review"
              ariaLabel="Reviews per day over the last 14 days"
              emptyLabel="No reviews yet in the last 14 days."
            />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card title="Cards due in the next 7 days" size="small">
            <MiniBarChart
              data={stats.dueNext7Days}
              color="#7c3aed"
              unit="card"
              firstIsToday
              ariaLabel="Cards due per day over the next 7 days"
              emptyLabel="Nothing due in the next 7 days."
            />
          </Card>
        </Col>
      </Row>

      <Card title="Your cards by stage" size="small">
        <StageBreakdownBar breakdown={stats.stateBreakdown} total={stats.totalCards} />
      </Card>
    </Flex>
  );
};
